import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
    Image, 
    Upload,
    Copy,
    Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { useDocsI18n } from '../../i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminMedia({ 
    media,
    token,
    onDelete,
    onUploadComplete
}) {
    const { t } = useDocsI18n();
    const copyMediaUrl = (m) => {
        const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${m.url}`;
        const markdownLink = `![${m.filename}](${imageUrl})`;
        navigator.clipboard.writeText(markdownLink);
        toast.success(t('imageCopied'));
    };

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        const formData = new FormData();
        formData.append('file', file);
        
        try {
            const headers = {};
            if (token && String(token).split('.').length === 3) headers.Authorization = `Bearer ${token}`;
            await fetch(`${API}/media`, {
                method: 'POST',
                body: formData,
                headers
            });
            toast.success(t('imageUploaded'));
            onUploadComplete?.();
        } catch (error) {
            toast.error('Upload fehlgeschlagen');
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            <div className="flex items-center justify-between mb-8">
                <h1 className="text-3xl font-mono font-bold">Medien</h1>
                <label>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleUpload}
                        className="hidden"
                        data-testid="media-upload-input"
                    />
                    <Button asChild>
                        <span>
                            <Upload className="w-4 h-4 mr-2" />
                            Bild hochladen
                        </span>
                    </Button>
                </label>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {media.map(m => (
                    <Card key={m.id} className="border-border/50 group overflow-hidden">
                        <div className="aspect-square bg-muted relative">
                            <img
                                src={`${process.env.REACT_APP_BACKEND_URL}${m.url}`}
                                alt={m.filename}
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                <Button variant="secondary" size="icon" onClick={() => copyMediaUrl(m)} title={t('copyImage')}>
                                    <Copy className="w-4 h-4" />
                                </Button>
                                <Button
                                    variant="destructive"
                                    size="icon"
                                    onClick={() => onDelete(m)}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                        <CardContent className="p-2">
                            <p className="text-xs font-mono truncate">{m.filename}</p>
                        </CardContent>
                    </Card>
                ))}
                {media.length === 0 && (
                    <div className="col-span-full text-center py-12 border border-dashed border-border rounded-xl">
                        <Image className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">{t('noMedia')}</p>
                    </div>
                )}
            </div>
        </motion.div>
    );
}
